import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';

const Register = () => {
    const [formData, setFormData] = useState({
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isRegistered, setIsRegistered] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsLoading(true);

        // Validate password match
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setIsLoading(false);
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            setIsLoading(false);
            return;
        }

        // Validate terms acceptance
        if (!acceptTerms) {
            setError('You must accept the Terms and Conditions to proceed');
            setIsLoading(false);
            return;
        }

        try {
            console.log('Sending registration request with data:', {
                username: formData.username,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName
            });

            const response = await axios.post(`${API_BASE_URL}/auth/register`, {
                username: formData.username,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                password: formData.password
            });

            console.log('Registration successful:', response.data);
            
            // Log verification link for debugging (development only)
            if (process.env.NODE_ENV === 'development' && response.data.verificationLink) {
                console.log('🔗 Verification link for testing:', response.data.verificationLink);
            }
            
            
            // Set success state and show verification message
            setIsRegistered(true);
            setUserEmail(formData.email);
            setSuccess(response.data.message);
            
            // Clear form data
            setFormData({
                username: '',
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                confirmPassword: ''
            });
            setAcceptTerms(false);
        } catch (error) {
            console.error('Registration error:', error.response || error);
            const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
            
            // Handle specific error cases
            if (errorMessage.includes('already registered and verified')) {
                setError('This email is already registered and verified. Please log in instead.');
            } else if (errorMessage.includes('Username already exists')) {
                setError('This username is already taken. Please choose a different username.');
            } else {
                setError(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendVerification = async () => {
        try {
            setError('');
            setIsLoading(true);
            const response = await axios.post(`${API_BASE_URL}/auth/resend-verification`, { email: userEmail });
            
            let message = 'Verification email sent! Please check your inbox.';
            if (response.data.tokenExpired) {
                message = 'Your previous verification link expired. A new verification email has been sent!';
            }
            
            setSuccess(message);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to send verification email.';
            
            // Handle specific error cases
            if (errorMessage.includes('User not found')) {
                setError('No account found with this email. Please register first.');
            } else if (errorMessage.includes('already verified')) {
                setError('This email is already verified. You can log in now.');
            } else {
                setError(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        // Redirect the user to the Google authentication endpoint on the backend
        window.location.href = `${API_BASE_URL}/auth/google`;
    };

    // If user is registered, show verification message
    if (isRegistered) {
        return (
            <div className="auth-form">
                <div className="verification-success">
                    <h2>Check Your Email!</h2>
                    <div className="email-icon">📧</div>
                    <p className="verification-message">
                        We've sent a verification email to <strong>{userEmail}</strong>
                    </p>
                    <p className="verification-instructions">
                        Please check your email and click the verification link to activate your account.
                    </p>
                    
                    <div className="verification-actions">
                        <button 
                            onClick={handleResendVerification}
                            className="resend-button"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <LoadingSpinner size="sm" color="primary" text="" />
                                    <span className="ml-2">Sending...</span>
                                </div>
                            ) : (
                                'Resend Verification Email'
                            )}
                        </button>
                        <Link to="/auth/login" className="login-link">
                            Back to Login
                        </Link>
                    </div>

                    {success && <div className="success-message">{success}</div>}
                    {error && <div className="error-message">{error}</div>}

                    <div className="verification-tips">
                        <h4>Can't find the email?</h4>
                        <ul>
                            <li>Check your spam/junk folder</li>
                            <li>Make sure you entered the correct email address</li>
                            <li>The verification link expires in 5 minutes</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    // Initial view: Google first, then "Register with email"
    if (!showEmailForm) {
        return (
            <div className="auth-form">
                <h2>Register</h2>
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}

                <div className="google-signin-container">
                    <button 
                        type="button" 
                        className="google-signin-btn" 
                        onClick={handleGoogleLogin}
                        disabled={isLoading}
                    >
                        <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google logo" />
                        Sign up with Google
                    </button>
                </div>

                <p className="or-divider"><span>OR</span></p>

                <button
                    type="button"
                    className="register-with-email-btn"
                    onClick={() => setShowEmailForm(true)}
                >
                    Register with email
                </button>

                <p className="auth-switch">
                    Already have an account? <Link to="/auth/login">Login here</Link>
                </p>
            </div>
        );
    }

    // Email registration form (shown after "Register with email" is clicked)
    return (
        <div className="auth-form">
            <h2>Register</h2>
            <button
                type="button"
                className="back-to-options-link"
                onClick={() => setShowEmailForm(false)}
            >
                ← Back
            </button>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            
            {isLoading && (
                <div className="registration-loading">
                    <LoadingSpinner 
                        size="lg" 
                        color="primary" 
                        text="Creating your account..." 
                    />
                    <p className="loading-text">
                        Please wait while we set up your account and send you a verification email.
                    </p>
                </div>
            )}
            
            <form onSubmit={handleSubmit} className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
                <div className="form-group">
                    <label htmlFor="username">Username</label>
                    <input
                        type="text"
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        autoComplete="given-name"
                        autoCapitalize="words"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        autoComplete="family-name"
                        autoCapitalize="words"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength="8"
                        disabled={isLoading}
                        autoComplete="new-password"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                        autoComplete="new-password"
                    />
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="form-group terms-checkbox-group">
                    <label className="terms-checkbox-label">
                        <input
                            type="checkbox"
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            disabled={isLoading}
                            required
                            className="terms-checkbox"
                        />
                        <span className="terms-text">
                                        I agree to the{' '}
                            <Link 
                                to="/terms-and-conditions" 
                                target="_blank" 
                                className="terms-link"
                            >
                                Terms and Conditions
                            </Link>
                            {' '}and understand that my content may be used by WhatToMake.co.za
                        </span>
                    </label>
                </div>

                <button type="submit" disabled={isLoading || !acceptTerms}>
                    {isLoading ? (
                        <div className="flex items-center justify-center">
                            <LoadingSpinner size="sm" color="primary" text="" />
                            <span className="ml-2">Creating Account...</span>
                        </div>
                    ) : (
                        'Register'
                    )}
                </button>
            </form>
            <p className="auth-switch">
                Already have an account? <Link to="/auth/login">Login here</Link>
            </p>
        </div>
    );
};

export default Register; 